import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../components/ui/sheet";
import { Plus, Trash2, MapPin, RotateCw, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const KIND_OPTIONS = [
  { value: "main", label: "Hauptlager", color: "#F97316" },
  { value: "buffer", label: "Zwischenlager", color: "#0EA5E9" },
  { value: "pickup", label: "Abhollager", color: "#9333EA" },
  { value: "return", label: "Rückbuchungsregal", color: "#2563EB" },
];

const kindOf = (s) => {
  if (s.kind) return s.kind;
  if (s.is_return_shelf) return "return";
  return "main";
};

const colorOfKind = (k) => KIND_OPTIONS.find((o) => o.value === k)?.color || "#F97316";

const defaultShelf = () => ({
  label: `R${Math.floor(Math.random() * 99) + 1}`,
  name: "",
  x: 120,
  y: 120,
  width: 220,
  height: 100,
  rotation: 0,
  rows: 2,
  cols: 4,
  color: "#F97316",
  kind: "main",
  is_return_shelf: false,
});

export default function Lagerplan() {
  const [shelves, setShelves] = useState([]);
  const [articles, setArticles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dragging, setDragging] = useState(null);
  const canvasRef = useRef(null);

  const fetchAll = async () => {
    const [s, a] = await Promise.all([api.get("/shelves"), api.get("/articles")]);
    setShelves(s.data);
    setArticles(a.data);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const addShelf = async () => {
    try {
      const { data } = await api.post("/shelves", defaultShelf());
      setShelves((s) => [...s, data]);
      toast.success(`Regal ${data.label} hinzugefügt.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const onMouseDown = (e, shelf) => {
    if (e.target.dataset?.role === "compartment") return;
    const rect = canvasRef.current.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    setDragging({
      id: shelf.id,
      origX: shelf.x,
      origY: shelf.y,
      startX,
      startY,
      moved: false,
    });
  };

  useEffect(() => {
    if (!dragging) return;
    const move = (e) => {
      const dx = e.clientX - dragging.startX;
      const dy = e.clientY - dragging.startY;
      if (Math.abs(dx) + Math.abs(dy) > 3) {
        setShelves((list) =>
          list.map((s) =>
            s.id === dragging.id ? { ...s, x: dragging.origX + dx, y: dragging.origY + dy } : s
          )
        );
        setDragging((d) => ({ ...d, moved: true }));
      }
    };
    const up = async () => {
      const shelf = shelves.find((s) => s.id === dragging.id);
      if (shelf && dragging.moved) {
        try {
          await api.put(`/shelves/${shelf.id}`, shelf);
        } catch (_) {}
      } else if (shelf && !dragging.moved) {
        setSelected(shelf);
      }
      setDragging(null);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, [dragging, shelves]);

  const updateShelf = async (id, patch) => {
    const next = shelves.map((s) => (s.id === id ? { ...s, ...patch } : s));
    setShelves(next);
    const target = next.find((s) => s.id === id);
    if (target) {
      try {
        await api.put(`/shelves/${id}`, target);
      } catch (_) {}
    }
  };

  const removeShelf = async (id) => {
    try {
      await api.delete(`/shelves/${id}`);
      setShelves((s) => s.filter((x) => x.id !== id));
      setSelected(null);
      toast.success("Regal entfernt.");
    } catch (e) {
      toast.error("Konnte Regal nicht löschen.");
    }
  };

  const articlesInCompartment = (shelfLabel, fachLabel) =>
    articles.filter(
      (a) => (a.shelf_label || "").toUpperCase() === shelfLabel.toUpperCase() &&
        (a.compartment || "").toUpperCase() === fachLabel.toUpperCase()
    );

  return (
    <div className="max-w-[1700px] mx-auto space-y-5" data-testid="floorplan-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="mono text-[11px] tracking-[0.25em] text-zinc-500 mb-2">
            2D LAGERPLAN · {shelves.length} REGALE
          </div>
          <h1 className="font-display text-4xl uppercase">Lagerplan</h1>
          <div className="text-sm text-zinc-500 mt-1">
            Regale ziehen, klicken um Fächer zu öffnen. Beschriftungen müssen mit den Artikeln übereinstimmen (R1/F03).
          </div>
        </div>
        <Button
          onClick={addShelf}
          data-testid="add-shelf-button"
          className="bg-[#F97316] hover:bg-[#EA580C] text-white rounded-sm h-11 px-5 font-semibold tracking-wide"
        >
          <Plus className="w-4 h-4 mr-2" /> REGAL HINZUFÜGEN
        </Button>
      </div>

      <div
        ref={canvasRef}
        className="blueprint-grid border border-zinc-300 rounded-sm relative overflow-hidden"
        style={{ height: "70vh", minHeight: "500px" }}
        data-testid="floorplan-canvas"
      >
        {shelves.map((s) => (
          <div
            key={s.id}
            data-testid={`shelf-${s.id}`}
            onMouseDown={(e) => onMouseDown(e, s)}
            className="absolute group select-none cursor-grab active:cursor-grabbing"
            style={{
              left: s.x,
              top: s.y,
              width: s.width,
              height: s.height,
              transform: `rotate(${s.rotation || 0}deg)`,
            }}
          >
            <div
              className="w-full h-full border-2 border-black bg-white shadow-[4px_4px_0_0_rgba(0,0,0,0.85)] flex flex-col"
              style={{
                borderLeftColor: colorOfKind(kindOf(s)),
                borderLeftWidth: 6,
              }}
            >
              <div
                className="px-2 py-1 text-white flex items-center justify-between"
                style={{ background: kindOf(s) === "main" ? "#000" : colorOfKind(kindOf(s)) }}
              >
                <span className="mono text-[10px] tracking-wider font-bold">
                  {kindOf(s) === "return" ? "↺ " : kindOf(s) === "buffer" ? "⊟ " : kindOf(s) === "pickup" ? "⇪ " : ""}
                  {s.label}
                </span>
                <span className="mono text-[9px] text-zinc-200">{s.rows}×{s.cols}</span>
              </div>
              <div
                className="flex-1 grid p-1 gap-px"
                style={{
                  gridTemplateColumns: `repeat(${s.cols}, minmax(0, 1fr))`,
                  gridTemplateRows: `repeat(${s.rows}, minmax(0, 1fr))`,
                }}
              >
                {Array.from({ length: s.rows * s.cols }).map((_, idx) => {
                  const fachIdx = idx + 1;
                  const customLbl = (s.compartment_labels?.[idx] || "").trim();
                  const label = customLbl || `F${String(fachIdx).padStart(2, "0")}`;
                  const items = articlesInCompartment(s.label, label);
                  const hasItems = items.length > 0;
                  return (
                    <div
                      key={idx}
                      data-role="compartment"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelected({ shelf: s, fach: label, items });
                      }}
                      className={`mono text-[9px] flex items-center justify-center cursor-pointer ${
                        hasItems ? "bg-orange-200 hover:bg-orange-300" : "bg-zinc-100 hover:bg-zinc-200"
                      } border border-zinc-300`}
                      title={`${s.label} · ${label}`}
                    >
                      <span className="leading-none">
                        {label}
                        {hasItems && (
                          <span className="ml-0.5 font-bold text-orange-700">·{items.length}</span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}

        {shelves.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <MapPin className="w-12 h-12 text-zinc-300 mx-auto mb-3" strokeWidth={1.5} />
              <div className="mono text-[11px] tracking-[0.25em] text-zinc-500">
                LEERE LAGERHALLE — KLICKE „REGAL HINZUFÜGEN"
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Detail sheet */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="rounded-sm sm:max-w-md">
          {selected && selected.fach ? (
            <>
              <SheetHeader>
                <div className="mono text-[10px] tracking-[0.25em] text-zinc-500">FACH</div>
                <SheetTitle className="font-display uppercase">
                  {selected.shelf.label} · {selected.fach}
                </SheetTitle>
                <SheetDescription>
                  {selected.items.length} Artikel an diesem Platz.
                </SheetDescription>
              </SheetHeader>
              <div className="mt-6 space-y-2">
                {selected.items.length === 0 && (
                  <div className="text-sm text-zinc-500">
                    Noch keine Artikel zugewiesen. Setze in einem Artikel Regal = „{selected.shelf.label}" und Fach = „{selected.fach}".
                  </div>
                )}
                {selected.items.map((a) => (
                  <Link
                    key={a.id}
                    to={`/artikel/${a.id}`}
                    className="block border border-zinc-200 rounded-sm p-3 hover:bg-zinc-50"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">{a.name}</div>
                        <div className="mono text-[11px] text-zinc-500">{a.ean}</div>
                      </div>
                      <div className="font-mono-num text-lg font-semibold">{a.quantity}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          ) : selected ? (
            <>
              <SheetHeader>
                <div className="mono text-[10px] tracking-[0.25em] text-zinc-500">REGAL</div>
                <SheetTitle className="font-display uppercase">{selected.label}</SheetTitle>
                <SheetDescription>Eigenschaften anpassen oder Regal entfernen.</SheetDescription>
              </SheetHeader>
              <div className="mt-6 space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">Bezeichnung</Label>
                  <Input
                    value={selected.label}
                    onChange={(e) => {
                      const v = e.target.value.toUpperCase();
                      setSelected({ ...selected, label: v });
                      updateShelf(selected.id, { label: v });
                    }}
                    data-testid="shelf-label-input"
                    className="mt-1.5 rounded-sm border-zinc-400 font-mono-num"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">Name (optional)</Label>
                  <Input
                    value={selected.name || ""}
                    onChange={(e) => {
                      setSelected({ ...selected, name: e.target.value });
                      updateShelf(selected.id, { name: e.target.value });
                    }}
                    className="mt-1.5 rounded-sm border-zinc-400"
                    placeholder="z.B. Schrauben & Muttern"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">Reihen</Label>
                    <Input
                      type="number"
                      min={1}
                      max={10}
                      value={selected.rows}
                      onChange={(e) => {
                        const v = Math.max(1, Math.min(10, Number(e.target.value) || 1));
                        setSelected({ ...selected, rows: v });
                        updateShelf(selected.id, { rows: v });
                      }}
                      data-testid="shelf-rows-input"
                      className="mt-1.5 rounded-sm border-zinc-400 font-mono-num"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">Spalten</Label>
                    <Input
                      type="number"
                      min={1}
                      max={20}
                      value={selected.cols}
                      onChange={(e) => {
                        const v = Math.max(1, Math.min(20, Number(e.target.value) || 1));
                        setSelected({ ...selected, cols: v });
                        updateShelf(selected.id, { cols: v });
                      }}
                      data-testid="shelf-cols-input"
                      className="mt-1.5 rounded-sm border-zinc-400 font-mono-num"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">Breite (px)</Label>
                    <Input
                      type="number"
                      value={selected.width}
                      onChange={(e) => {
                        const v = Number(e.target.value) || 100;
                        setSelected({ ...selected, width: v });
                        updateShelf(selected.id, { width: v });
                      }}
                      className="mt-1.5 rounded-sm border-zinc-400 font-mono-num"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">Höhe (px)</Label>
                    <Input
                      type="number"
                      value={selected.height}
                      onChange={(e) => {
                        const v = Number(e.target.value) || 60;
                        setSelected({ ...selected, height: v });
                        updateShelf(selected.id, { height: v });
                      }}
                      className="mt-1.5 rounded-sm border-zinc-400 font-mono-num"
                    />
                  </div>
                </div>

                {/* Fach-Namen individuell vergeben */}
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">
                    Fach-Namen ({selected.rows * selected.cols})
                  </Label>
                  <p className="text-[10px] text-zinc-500 mt-1 mb-2">
                    Leer lassen für Standard (F01, F02 …)
                  </p>
                  <div
                    className="grid gap-1.5"
                    style={{ gridTemplateColumns: `repeat(${Math.min(selected.cols, 4)}, minmax(0, 1fr))` }}
                  >
                    {Array.from({ length: selected.rows * selected.cols }).map((_, idx) => {
                      const labels = Array.isArray(selected.compartment_labels)
                        ? [...selected.compartment_labels]
                        : Array(selected.rows * selected.cols).fill("");
                      while (labels.length < selected.rows * selected.cols) labels.push("");
                      return (
                        <Input
                          key={idx}
                          value={labels[idx] || ""}
                          placeholder={`F${String(idx + 1).padStart(2, "0")}`}
                          onChange={(e) => {
                            const v = e.target.value;
                            const newLabels = [...labels];
                            newLabels[idx] = v;
                            setSelected({ ...selected, compartment_labels: newLabels });
                          }}
                          onBlur={() => {
                            updateShelf(selected.id, { compartment_labels: labels });
                          }}
                          data-testid={`compartment-label-${idx}`}
                          className="rounded-sm border-zinc-400 font-mono-num text-xs h-8 text-center"
                        />
                      );
                    })}
                  </div>
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">
                    Drehung ({Math.round(selected.rotation || 0)}°)
                  </Label>
                  <div className="grid grid-cols-5 gap-2 mt-1.5">
                    {[0, 45, 90, 135, 180].map((deg) => (
                      <button
                        key={deg}
                        onClick={() => {
                          setSelected({ ...selected, rotation: deg });
                          updateShelf(selected.id, { rotation: deg });
                        }}
                        data-testid={`rotate-${deg}`}
                        className={`h-9 rounded-sm border text-xs mono ${
                          Math.round(selected.rotation || 0) === deg
                            ? "bg-black text-white border-black"
                            : "bg-white border-zinc-400 hover:bg-zinc-50"
                        }`}
                      >
                        {deg}°
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <button
                      onClick={() => {
                        const v = ((selected.rotation || 0) - 15 + 360) % 360;
                        setSelected({ ...selected, rotation: v });
                        updateShelf(selected.id, { rotation: v });
                      }}
                      data-testid="rotate-left-15"
                      className="flex items-center gap-1 px-3 h-8 border border-zinc-400 rounded-sm text-xs hover:bg-zinc-50"
                    >
                      <RotateCcw className="w-3.5 h-3.5" /> −15°
                    </button>
                    <input
                      type="range"
                      min={0}
                      max={359}
                      value={selected.rotation || 0}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setSelected({ ...selected, rotation: v });
                      }}
                      onMouseUp={() => updateShelf(selected.id, { rotation: selected.rotation })}
                      onTouchEnd={() => updateShelf(selected.id, { rotation: selected.rotation })}
                      className="flex-1"
                      data-testid="rotation-slider"
                    />
                    <button
                      onClick={() => {
                        const v = ((selected.rotation || 0) + 15) % 360;
                        setSelected({ ...selected, rotation: v });
                        updateShelf(selected.id, { rotation: v });
                      }}
                      data-testid="rotate-right-15"
                      className="flex items-center gap-1 px-3 h-8 border border-zinc-400 rounded-sm text-xs hover:bg-zinc-50"
                    >
                      +15° <RotateCw className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 border border-zinc-200 rounded-sm bg-zinc-50 gap-3">
                  <div className="flex-1">
                    <div className="text-sm font-medium">Regal-Typ</div>
                    <div className="text-[11px] text-zinc-500">
                      Hauptlager = normales Lager. Zwischenlager = für Projekte reserviert.
                      Abhollager = Mitnehmen am Folgetag. Rückbuchung = nur visuell.
                    </div>
                  </div>
                  <Select
                    value={kindOf(selected)}
                    onValueChange={(v) => {
                      const next = { ...selected, kind: v, is_return_shelf: v === "return" };
                      setSelected(next);
                      updateShelf(selected.id, { kind: v, is_return_shelf: v === "return" });
                    }}
                  >
                    <SelectTrigger className="rounded-sm border-zinc-400 w-44" data-testid="shelf-kind-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-sm">
                      {KIND_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="pt-4 border-t border-zinc-200 flex justify-end">
                  <Button
                    onClick={() => removeShelf(selected.id)}
                    data-testid="delete-shelf-button"
                    className="bg-red-600 hover:bg-red-700 text-white rounded-sm"
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Regal löschen
                  </Button>
                </div>
              </div>
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}
